const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL ?? 'http://127.0.0.1:8787';

export type StoreTargetRow = {
  storeId: number;
  storeName: string;
  termNo: number;
  month: number;
  sales: number;
  profit: number;
  source: string;
  updatedAt: string;
};

export type StoreTargetListResponse = {
  rows: StoreTargetRow[];
  termNo: number;
  activeTermNo: number;
  terms: { termNo: number }[];
};

export type StoreHalfScorePeriod = 'first_half' | 'second_half';

export type StoreHalfScoreRow = {
  storeId: number;
  storeName: string;
  termNo: number;
  period: StoreHalfScorePeriod;
  score: number;
  scoreDetail?: Record<string, unknown>;
  source: string;
  reportDate: string;
  updatedAt: string;
};

export type StoreHalfScoreListResponse = {
  rows: StoreHalfScoreRow[];
  termNo: number;
  activeTermNo: number;
  terms: { termNo: number }[];
};

export type StoreScorePdfPreview = {
  storeId: number;
  storeName: string;
  termNo: number;
  period: StoreHalfScorePeriod;
  score: number;
  scoreDetail?: Record<string, unknown>;
  reportDate: string;
};

async function throwIfNotOk(response: Response) {
  if (response.ok) return;
  const payload = await response.json().catch(() => ({}));
  const message = typeof payload.message === 'string' ? payload.message : 'API request failed';
  throw new Error(message);
}

export async function fetchStoreTargetRows(termNo?: number) {
  const query = typeof termNo === 'number' ? `?${new URLSearchParams({ termNo: String(termNo) }).toString()}` : '';
  const response = await fetch(`${apiBaseUrl}/api/admin/store-targets${query}`);
  await throwIfNotOk(response);
  return (await response.json()) as StoreTargetListResponse;
}

export async function saveStoreTargetRows(input: {
  rows: {
    storeId: number;
    termNo: number;
    month: number;
    sales: number;
    profit: number;
  }[];
}) {
  const response = await fetch(`${apiBaseUrl}/api/admin/store-targets`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...input, source: 'manual' }),
  });
  await throwIfNotOk(response);
  return response.json() as Promise<{ result: { count: number } }>;
}

async function toBase64(file: File) {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

async function postStoreScorePdfFormData(
  endpointPath: string,
  file: File,
  options?: {
    termNo?: number;
    period?: StoreHalfScorePeriod;
  },
) {
  const formData = new FormData();
  formData.append('file', file, file.name);
  formData.append('fileName', file.name);
  if (typeof options?.termNo === 'number') {
    formData.append('termNo', String(options.termNo));
  }
  if (options?.period) {
    formData.append('period', options.period);
  }

  return fetch(`${apiBaseUrl}${endpointPath}`, {
    method: 'POST',
    body: formData,
  });
}

export async function previewStoreTargetPdf(file: File) {
  const fileBase64 = await toBase64(file);
  const response = await fetch(`${apiBaseUrl}/api/admin/store-targets/import-pdf/preview`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      fileName: file.name,
      fileBase64,
    }),
  });
  await throwIfNotOk(response);
  const payload = (await response.json()) as {
    result: {
      termNo: number;
      month: number;
      availableMonths: number[];
      storeCount: number;
    };
  };
  return payload.result;
}

export async function importStoreTargetPdf(file: File, targetMonths: number[]) {
  const fileBase64 = await toBase64(file);

  const response = await fetch(`${apiBaseUrl}/api/admin/store-targets/import-pdf`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      fileName: file.name,
      fileBase64,
      targetMonths,
    }),
  });

  await throwIfNotOk(response);
  const payload = (await response.json()) as {
    result: {
      termNo: number;
      month: number;
      importedMonths: number[];
      importedCount: number;
      rows: StoreTargetRow[];
      unmatchedStoreNames: string[];
    };
  };
  return payload.result;
}

export async function fetchStoreQscScoreRows(termNo?: number) {
  const query = typeof termNo === 'number' ? `?${new URLSearchParams({ termNo: String(termNo) }).toString()}` : '';
  const response = await fetch(`${apiBaseUrl}/api/admin/store-targets/monitor-check${query}`);
  await throwIfNotOk(response);
  return (await response.json()) as StoreHalfScoreListResponse;
}

export async function importStoreQscPdf(file: File) {
  const response = await postStoreScorePdfFormData(
    '/api/admin/store-targets/monitor-check/import-pdf',
    file,
  );
  await throwIfNotOk(response);
  const payload = (await response.json()) as {
    result: {
      storeId: number;
      storeName: string;
      termNo: number;
      period: StoreHalfScorePeriod;
      score: number;
      reportDate: string;
    };
  };
  return payload.result;
}

export async function importStoreQscPdfWithPeriod(file: File, termNo: number, period: StoreHalfScorePeriod) {
  const response = await postStoreScorePdfFormData(
    '/api/admin/store-targets/monitor-check/import-pdf',
    file,
    { termNo, period },
  );
  await throwIfNotOk(response);
  const payload = (await response.json()) as {
    result: {
      storeId: number;
      storeName: string;
      termNo: number;
      period: StoreHalfScorePeriod;
      score: number;
      reportDate: string;
    };
  };
  return payload.result;
}

export async function previewStoreQscPdf(file: File) {
  let response: Response;
  try {
    response = await postStoreScorePdfFormData(
      '/api/admin/store-targets/monitor-check/import-pdf/preview',
      file,
    );
  } catch (error) {
    const fileSizeMb = (file.size / (1024 * 1024)).toFixed(1);
    const isNetworkError =
      error instanceof TypeError &&
      /failed to fetch|network/i.test(error.message);
    if (isNetworkError) {
      throw new Error(
        `モニターチェックPDFの通信に失敗しました。ファイルサイズ=${fileSizeMb}MB。環境側の通信制限（プロキシ/セキュリティ/アップロード上限）の可能性があります。`,
      );
    }
    throw error;
  }
  await throwIfNotOk(response);
  const payload = (await response.json()) as {
    result: StoreScorePdfPreview;
  };
  return payload.result;
}

export async function saveStoreQscScoreRows(input: {
  rows: {
    storeId: number;
    termNo: number;
    period: StoreHalfScorePeriod;
    score: number;
    scoreDetail?: Record<string, unknown>;
    reportDate: string;
  }[];
}) {
  const response = await fetch(`${apiBaseUrl}/api/admin/store-targets/monitor-check`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });
  await throwIfNotOk(response);
  return response.json() as Promise<{ result: { count: number } }>;
}

export async function fetchStoreQscRateRows(termNo?: number) {
  const query = typeof termNo === 'number' ? `?${new URLSearchParams({ termNo: String(termNo) }).toString()}` : '';
  const response = await fetch(`${apiBaseUrl}/api/admin/store-targets/qsc${query}`);
  await throwIfNotOk(response);
  return (await response.json()) as StoreHalfScoreListResponse;
}

export async function previewStoreQscRatePdf(file: File) {
  const response = await postStoreScorePdfFormData('/api/admin/store-targets/qsc/import-pdf/preview', file);
  await throwIfNotOk(response);
  const payload = (await response.json()) as {
    result: StoreScorePdfPreview;
  };
  return payload.result;
}

export async function importStoreQscRatePdf(file: File) {
  const response = await postStoreScorePdfFormData('/api/admin/store-targets/qsc/import-pdf', file);
  await throwIfNotOk(response);
  const payload = (await response.json()) as {
    result: {
      storeId: number;
      storeName: string;
      termNo: number;
      period: StoreHalfScorePeriod;
      score: number;
      reportDate: string;
    };
  };
  return payload.result;
}

export async function importStoreQscRatePdfWithPeriod(
  file: File,
  termNo: number,
  period: StoreHalfScorePeriod,
) {
  const response = await postStoreScorePdfFormData(
    '/api/admin/store-targets/qsc/import-pdf',
    file,
    { termNo, period },
  );
  await throwIfNotOk(response);
  const payload = (await response.json()) as {
    result: {
      storeId: number;
      storeName: string;
      termNo: number;
      period: StoreHalfScorePeriod;
      score: number;
      reportDate: string;
    };
  };
  return payload.result;
}

export async function saveStoreQscRateRows(input: {
  rows: {
    storeId: number;
    termNo: number;
    period: StoreHalfScorePeriod;
    score: number;
    scoreDetail?: Record<string, unknown>;
    reportDate: string;
  }[];
}) {
  const response = await fetch(`${apiBaseUrl}/api/admin/store-targets/qsc`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });
  await throwIfNotOk(response);
  return response.json() as Promise<{ result: { count: number } }>;
}

export async function fetchStoreCrewEsScoreRows(termNo?: number) {
  const query = typeof termNo === 'number' ? `?${new URLSearchParams({ termNo: String(termNo) }).toString()}` : '';
  const response = await fetch(`${apiBaseUrl}/api/admin/store-targets/crew-es${query}`);
  await throwIfNotOk(response);
  return (await response.json()) as StoreHalfScoreListResponse;
}

export async function importStoreCrewEsPdf(file: File) {
  const response = await postStoreScorePdfFormData('/api/admin/store-targets/crew-es/import-pdf', file);
  await throwIfNotOk(response);
  const payload = (await response.json()) as {
    result: {
      storeId: number;
      storeName: string;
      termNo: number;
      period: StoreHalfScorePeriod;
      score: number;
      reportDate: string;
    };
  };
  return payload.result;
}

export async function importStoreCrewEsPdfWithPeriod(file: File, termNo: number, period: StoreHalfScorePeriod) {
  const response = await postStoreScorePdfFormData('/api/admin/store-targets/crew-es/import-pdf', file, {
    termNo,
    period,
  });
  await throwIfNotOk(response);
  const payload = (await response.json()) as {
    result: {
      storeId: number;
      storeName: string;
      termNo: number;
      period: StoreHalfScorePeriod;
      score: number;
      reportDate: string;
    };
  };
  return payload.result;
}

export async function previewStoreCrewEsPdf(file: File) {
  const response = await postStoreScorePdfFormData('/api/admin/store-targets/crew-es/import-pdf/preview', file);
  await throwIfNotOk(response);
  const payload = (await response.json()) as {
    result: StoreScorePdfPreview;
  };
  return payload.result;
}

export async function saveStoreCrewEsScoreRows(input: {
  rows: {
    storeId: number;
    termNo: number;
    period: StoreHalfScorePeriod;
    score: number;
    scoreDetail?: Record<string, unknown>;
    reportDate: string;
  }[];
}) {
  const response = await fetch(`${apiBaseUrl}/api/admin/store-targets/crew-es`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });
  await throwIfNotOk(response);
  return response.json() as Promise<{ result: { count: number } }>;
}
